import { useState } from 'react';

import { Check, TriangleAlert } from 'lucide-react';

import { SimpleTooltip } from '@/components/ui/simple-tooltip';
import i18n from '@/core/i18n';
import { EntityAvatar, StatusBadge, StatusBadgeType } from '@/modules/common';
import { ScrollableText } from '@/modules/planner/components/assignment/text/scrollable-text';
import { DEFAULT_PROJECT_COLOR_CSS_VAR } from '@/modules/projects/constants/project-color';
import { ProjectStatus } from '@/modules/projects/types/project';
import { formatDisplayDate } from '@/utils/date-formatters';

const SCROLL_ANIMATION_DELAY_MS = 100;

/**
 * Minimal project shape shared by `SingleSelectOption` and `TableFilterOption`.
 * Both carry the same fields the project dropdown needs.
 */
export interface ProjectDropdownOption {
  label: string;
  color?: string;
  avatar?: string | null;
  status?: string | null;
  nextArchiveDate?: string | null;
  nextUnarchiveDate?: string | null;
  /** True if the member has a matching booking on this project (time-tracker project picker). */
  hasRelevantAssignments?: boolean;
}

const isArchived = (status?: string | null): boolean => status === ProjectStatus.Archived;

/**
 * Tooltip text for the scheduled archive/unarchive warning icon. Unarchive date takes
 * priority (most relevant for an archived project is when it comes back), then archive
 * date. Returns null when no scheduled change exists.
 */
const getScheduledChangeTooltip = (option: ProjectDropdownOption): string | null => {
  // Use the `common` namespace (always loaded) — the dropdown renders in contexts
  // (planner, time-tracking, finance) that don't load the lazy `authenticated.projects`
  // namespace, which showed the raw `scheduledArchive.willBeArchivedOn` key as the tooltip.
  if (option.nextUnarchiveDate) {
    return i18n.t('common:projectWillBeUnarchivedOn', {
      date: formatDisplayDate(option.nextUnarchiveDate),
    });
  }
  if (option.nextArchiveDate) {
    return i18n.t('common:projectWillBeArchivedOn', {
      date: formatDisplayDate(option.nextArchiveDate),
    });
  }
  return null;
};

interface ProjectColorDotProps {
  color?: string;
}

const ProjectColorDot = ({ color }: ProjectColorDotProps) => (
  <div
    className="h-1.5 w-1.5 shrink-0 rounded-full"
    style={{ backgroundColor: color || DEFAULT_PROJECT_COLOR_CSS_VAR }}
  />
);

interface ScheduledChangeWarningProps {
  option: ProjectDropdownOption;
}

/**
 * Small warning triangle shown for ANY project (regardless of status) that has a
 * scheduled archive/unarchive. Hovering shows the date. `stopPropagation` so hovering
 * the icon's tooltip doesn't interfere with the row's scroll-on-hover.
 */
const ScheduledChangeWarning = ({ option }: ScheduledChangeWarningProps) => {
  const tooltip = getScheduledChangeTooltip(option);
  if (!tooltip) return null;

  // Wrap the icon in a focusable span: SimpleTooltip uses Radix `asChild`, which needs a
  // real DOM element that reliably receives pointer/focus events to open the tooltip — a
  // bare SVG doesn't. `inline-flex` keeps the icon aligned with the row.
  return (
    <SimpleTooltip content={tooltip}>
      <span className="inline-flex shrink-0" tabIndex={0}>
        <TriangleAlert className="h-3.5 w-3.5 text-warning" />
      </span>
    </SimpleTooltip>
  );
};

/**
 * "Archived" badge. Uses the `common:archived` copy (always loaded) rather than the
 * lazily-loaded `authenticated.projects` namespace — the latter showed the raw
 * `status.archived` key in dropdowns rendered before that namespace was fetched.
 * `status` is still passed to `StatusBadge` purely for its color styling.
 */
const ArchivedBadge = () => (
  <StatusBadge
    type={StatusBadgeType.Project}
    status={ProjectStatus.Archived}
    label={i18n.t('common:archived')}
  />
);
// NOTE: `common:archived` is a top-level key in common.json (always loaded). We avoid
// getProjectStatusText here because it resolves via the lazily-loaded authenticated.projects
// namespace, which showed the raw `status.archived` key in dropdowns before that ns loaded.

interface ProjectOptionContentProps {
  option: ProjectDropdownOption;
  /** Show the project logo avatar before the color dot (used by some filters). */
  showAvatar?: boolean;
  /** Show a trailing checkmark (single-select option when selected). */
  showCheck?: boolean;
  isSelected?: boolean;
}

/**
 * Shared option row: color dot (+ optional avatar) + scroll-on-hover name + "Archived"
 * badge (archived only) + scheduled-change warning icon. The name uses `ScrollableText`
 * so long project names scroll on hover — wrapped in an `overflow-hidden` clip box per
 * ScrollableText's contract — while the badge and warning icon stay fixed beside it.
 */
const ProjectOptionContent = ({
  option,
  showAvatar = false,
  showCheck = false,
  isSelected = false,
}: ProjectOptionContentProps) => {
  const [hovered, setHovered] = useState(false);

  return (
    <div
      className="flex w-full min-w-0 items-center justify-between gap-2"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div className="flex min-w-0 flex-1 items-center gap-2 overflow-hidden">
        {showAvatar && (
          <EntityAvatar name={option.label} image={option.avatar} size="xs" shape="rounded" />
        )}
        <ProjectColorDot color={option.color} />
        <div className="min-w-0 flex-1 overflow-hidden">
          <ScrollableText
            className="text-sm"
            externalHovered={hovered}
            animationDelayMs={SCROLL_ANIMATION_DELAY_MS}
          >
            {option.label}
          </ScrollableText>
        </div>
        {isArchived(option.status) && <ArchivedBadge />}
        <ScheduledChangeWarning option={option} />
      </div>
      {showCheck && isSelected && <Check className="h-4 w-4 shrink-0" />}
    </div>
  );
};

/**
 * Option renderer for project single-selects + paginated single-selects.
 * `showCheck` renders a trailing checkmark when selected (time-entry modal).
 */
export const renderProjectDropdownOption = (
  option: ProjectDropdownOption,
  { showCheck = false, isSelected = false }: { showCheck?: boolean; isSelected?: boolean } = {}
) => <ProjectOptionContent option={option} showCheck={showCheck} isSelected={isSelected} />;

/**
 * Trigger (selected-value) renderer for project single-selects. Truncates the name
 * (no scroll needed in the compact trigger) and shows the "Archived" badge for archived
 * projects. The scheduled warning is kept inside the dropdown list only.
 */
export const renderProjectDropdownTrigger = (
  selectedOption: ProjectDropdownOption | null,
  placeholder: string
) => (
  <div className="flex w-full items-center">
    {selectedOption ? (
      <div className="flex min-w-0 flex-1 items-center gap-2">
        <ProjectColorDot color={selectedOption.color} />
        <span className="truncate text-sm font-normal">{selectedOption.label}</span>
        {isArchived(selectedOption.status) && <ArchivedBadge />}
      </div>
    ) : (
      <span className="truncate text-sm font-normal text-muted-foreground">{placeholder}</span>
    )}
  </div>
);

type ProjectFilterOption = Pick<
  ProjectDropdownOption,
  'color' | 'avatar' | 'status' | 'nextArchiveDate' | 'nextUnarchiveDate'
>;

/**
 * filters-v2 `renderLabel(value, option)` adapter — color dot + scroll-on-hover name +
 * archived badge + warning. No avatar.
 */
export const renderProjectFilterLabel = (value: string, option: ProjectFilterOption) => (
  <ProjectOptionContent option={{ ...option, label: value }} />
);

/**
 * filters-v2 `renderLabel` adapter that also shows the project logo avatar.
 */
export const renderProjectFilterLabelWithAvatar = (value: string, option: ProjectFilterOption) => (
  <ProjectOptionContent option={{ ...option, label: value }} showAvatar />
);
